import { Button } from "@/components/ui/button";
import {
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { MapStateProvider } from "@/contexts/MapStateContext";
import {
  engineColumns,
  hasRpmRanges,
  type EngineDataLevel,
} from "@/lib/engine-data-level";
import {
  formatDateOnly,
  formatDateTime,
  formatKm,
  secondsToTime,
} from "@/lib/utils";
import SpeedChart from "@/pages/MyFleet/VehicleOverview/SpeedChart";
import type {
  VehicleJourneyData,
  VehicleJourneyReportTotals,
} from "@/services/api/trackReports";
import { ChevronDown, ChevronUp, Map } from "lucide-react";
import React from "react";

interface VehicleJourneyReportTableProps {
  vehicleJourneys: VehicleJourneyData[];
  handleMapButtonClick: (journey: VehicleJourneyData) => void;
  toggleRowExpand: (date: string) => void;
  expandedRows: Record<string, boolean>;
  totals: VehicleJourneyReportTotals;
  level: EngineDataLevel;
}

/** Colunas que não dependem do nível de dado de motor do veículo. */
const FIXED_COLUMN_COUNT = 11;

/** O travessão marca "não medimos isso", que não é a mesma coisa que zero. */
const NO_READING = "—";

const VehicleJourneyReportTable = ({
  vehicleJourneys,
  handleMapButtonClick,
  toggleRowExpand,
  expandedRows,
  totals,
  level,
}: VehicleJourneyReportTableProps) => {
  const columns = engineColumns(level);
  const columnCount = FIXED_COLUMN_COUNT + columns.length;

  const renderSeconds = (value: number | null) =>
    value === null ? NO_READING : secondsToTime(value);

  return (
    <div className="relative overflow-hidden border rounded-md">
      <div className="overflow-auto max-h-[600px]">
        <table className="relative text-sm w-full">
          <TableHeader className="sticky top-0 z-10 bg-background">
            <TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead className="w-10"></TableHead>
              <TableHead className="text-center">Data</TableHead>
              <TableHead className="text-center">Início</TableHead>
              <TableHead className="text-center">Fim</TableHead>
              <TableHead className="text-center">Movimento</TableHead>
              {columns.map((column) => (
                <TableHead key={column.key} className="text-center">
                  {column.label}
                </TableHead>
              ))}
              <TableHead className="text-center">Desligado</TableHead>
              <TableHead className="text-center">Km total</TableHead>
              <TableHead className="text-center">Km extra</TableHead>
              <TableHead className="text-center">Vel. máxima</TableHead>
              <TableHead className="text-center">Excesso vel.</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {vehicleJourneys?.map((journey) => {
              const isExpanded = journey.date
                ? expandedRows[journey.date]
                : false;

              const speedChartData = journey.chart_data.map((item) => ({
                start_hour: item.start_hour ?? "",
                end_hour: item.end_hour ?? "",
                hour: item.hour ?? "",
                speed: item.speed ?? 0,
                rpm: item.rpm ?? 0,
                label: item.label ?? "",
                is_stop: item.is_stop ?? false,
                stop_time: item.stop_time ?? 0,
              }));

              return (
                <React.Fragment key={journey.date}>
                  <TableRow className="dark:text-white">
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleRowExpand(journey.date ?? "")}
                        className="p-0 h-8 w-8"
                      >
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        className="dark:text-white dark:bg-muted"
                        size="sm"
                        variant="ghost"
                        onClick={() => handleMapButtonClick(journey)}
                      >
                        <MapStateProvider>
                          <Map className="w-6 h-6" />
                        </MapStateProvider>
                      </Button>
                    </TableCell>
                    <TableCell className="text-center">
                      {formatDateOnly(journey.date ?? "")}
                    </TableCell>
                    <TableCell className="text-center">
                      {formatDateTime(journey.first_move, true)}
                    </TableCell>
                    <TableCell className="text-center">
                      {formatDateTime(journey.last_move, true)}
                    </TableCell>
                    <TableCell className="text-center">
                      {secondsToTime(journey.timings.movement)}
                    </TableCell>
                    {columns.map((column) => (
                      <TableCell key={column.key} className="text-center">
                        {renderSeconds(column.seconds(journey.timings))}
                      </TableCell>
                    ))}
                    <TableCell className="text-center">
                      {secondsToTime(journey.timings.off)}
                    </TableCell>
                    <TableCell className="text-center">
                      {formatKm(journey.distance)} km
                    </TableCell>
                    <TableCell className="text-center">
                      {formatKm(journey.out_work_distance)} km
                    </TableCell>
                    <TableCell className="text-center">
                      {journey.max_speed} km/h
                    </TableCell>
                    <TableCell className="text-center">
                      {secondsToTime(journey.timings.over_speed)}
                    </TableCell>
                  </TableRow>

                  {/* Expanded row with chart */}
                  {isExpanded && (
                    <TableRow>
                      <TableCell
                        colSpan={columnCount}
                        className="p-4 border-t-0"
                      >
                        <SpeedChart
                          data={speedChartData}
                          start_hour={speedChartData[0]?.start_hour ?? ""}
                          end_hour={
                            speedChartData[speedChartData.length - 1]
                              ?.end_hour ?? ""
                          }
                          hasCanTelemetry={hasRpmRanges(level)}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>

          <TableFooter>
            <TableRow className="dark:text-white">
              <TableCell colSpan={5} className="text-left">
                Total
              </TableCell>
              <TableCell className="text-center">
                {totals.timings.movement
                  ? secondsToTime(totals.timings.movement)
                  : "-"}
              </TableCell>
              {columns.map((column) => (
                <TableCell key={column.key} className="text-center">
                  {renderSeconds(column.seconds(totals.timings))}
                </TableCell>
              ))}
              <TableCell className="text-center"></TableCell>
              <TableCell className="text-center">
                {formatKm(totals.distance)} km
              </TableCell>
              <TableCell className="text-center">
                {formatKm(totals.out_work_distance)} km
              </TableCell>
              <TableCell className="text-center">
                {totals.max_speed} Km/h
              </TableCell>
              <TableCell className="text-center">
                {totals.timings.over_speed
                  ? secondsToTime(totals.timings.over_speed)
                  : "00:00"}
              </TableCell>
            </TableRow>
          </TableFooter>
        </table>
      </div>
    </div>
  );
};

export default VehicleJourneyReportTable;
